import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { ActionBar } from './actions';

// THE INSPECTOR — whatever is open, on the page's right. Every item in Nucleus
// opens here (an app, a plugin, a person, a group, a realm, a grant, a node, an
// entry): its face and name at the head, its actions in one ActionBar under it,
// then what it is, in bands. The page never navigates away to show an item.
export default function Inspector({ face, title, sub, tag, actions, onClose, children, label }) {
    return (
        <aside className="nx-inspector" aria-label={label || (typeof title === 'string' ? title : 'Details')}>
            {/* its header strip lines up with the page's: what it is, and the way out */}
            <div className="nx-inspector__bar">
                <span className="nx-inspector__tag">{tag || 'Details'}</span>
                {onClose && (
                    <button type="button" className="nx-inspector__close" aria-label="Close" onClick={onClose}>
                        <X size={15} strokeWidth={1.7} aria-hidden="true" />
                    </button>
                )}
            </div>
            <div className="nx-inspector__head">
                {face && <div className="nx-inspector__face">{face}</div>}
                <div className="nx-inspector__names">
                    <h2 className="nx-inspector__title">{title}</h2>
                    {sub && <p className="nx-inspector__sub">{sub}</p>}
                </div>
            </div>
            <ActionBar actions={actions} />
            <div className="nx-inspector__body">{children}</div>
        </aside>
    );
}

// A titled band of the inspector.
export function Band({ title, aside, children }) {
    return (
        <section className="nx-band">
            <BandHead title={title} aside={aside} />
            <div className="nx-band__body">{children}</div>
        </section>
    );
}

// A band's head: its name, and a door or two at its right (Self's forms wear it too).
const has = (v) => v != null && v !== false && v !== '';
export function BandHead({ title, aside }) {
    if (!title && !has(aside)) return null;
    return (
        <div className="nx-band__head">
            {title && <span className="nx-band__title">{title}</span>}
            {has(aside) && <span className="nx-band__aside">{aside}</span>}
        </div>
    );
}

// Facts: label over value, in a grid of equal cells.
export function Facts({ items }) {
    const list = (items || []).filter(Boolean);
    if (!list.length) return null;
    return (
        <dl className="nx-facts">
            {list.map((f) => (
                <div key={f.label} className={`nx-fact${f.tone ? ` is-${f.tone}` : ''}`}>
                    <dt>{f.label}</dt>
                    <dd title={typeof f.value === 'string' ? f.value : undefined}>{f.value ?? '—'}</dd>
                </div>
            ))}
        </dl>
    );
}

// A quiet line of prose inside a band.
export function Note({ children, tone }) {
    return <p className={`nx-note${tone ? ` is-${tone}` : ''}`}>{children}</p>;
}

// Chips of short words (verbs, kinds, realms). `onPress` makes one a door.
export function Tags({ items }) {
    const list = (items || []).filter(Boolean);
    if (!list.length) return null;
    return (
        <div className="nx-tags">
            {list.map((t) => (t.onPress
                ? <button key={t.key || t.label} type="button" className={`nx-tag is-door${t.tone ? ` is-${t.tone}` : ''}`} onClick={t.onPress}>{t.label}</button>
                : <span key={t.key || t.label} className={`nx-tag${t.tone ? ` is-${t.tone}` : ''}`}>{t.label}</span>))}
        </div>
    );
}

// A LINK TO AN ITEM THE LIST DOES NOT HOLD. An ?open= id missing from the
// loaded list used to open nothing and say nothing. Once the list is in
// (`ready`) and the id is not in it (`inList` false), the page reads it on its
// own: `read(id)` resolves to the item when it is there (the page then reads
// its list again, or shows it) or null when it is not. What comes back is
// { state: 'reading' | 'found' | 'missing' | 'error', message } for the id
// asked, or null when there is nothing to say.
export function useLinkedItem(openId, { ready, inList, read }) {
    const [miss, setMiss] = useState(null);
    const readRef = useRef(read);
    readRef.current = read;
    useEffect(() => {
        if (!openId || !ready || inList) return undefined;
        let live = true;
        setMiss({ id: openId, state: 'reading' });
        Promise.resolve()
            .then(() => readRef.current?.(openId))
            .then((item) => { if (live) setMiss({ id: openId, state: item ? 'found' : 'missing' }); })
            .catch((e) => { if (live) setMiss({ id: openId, state: 'error', message: e?.message || null }); });
        return () => { live = false; };
    }, [openId, ready, inList]);
    return !inList && miss && String(miss.id) === String(openId) ? miss : null;
}

// Which item is open — one search param, the same on every page (?open=<id>),
// so a link can open any item and Back closes it.
export function useOpen(param = 'open') {
    const [params, setParams] = useSearchParams();
    const id = params.get(param);
    const open = useCallback((next) => setParams((p) => {
        const n = new URLSearchParams(p);
        if (next == null || String(next) === p.get(param)) n.delete(param);
        else n.set(param, String(next));
        return n;
    }), [param, setParams]);
    const close = useCallback(() => setParams((p) => {
        const n = new URLSearchParams(p);
        n.delete(param);
        return n;
    }), [param, setParams]);
    return [id, open, close];
}

// AN OPEN ITEM, FROM THE LIST OR READ ON ITS OWN — useLinkedItem's adoption:
// the item the list holds for `openId`, else (once the list is in) the one
// `read(id)` resolves to, kept; and a `notice` to say when it is not there
// ({ tone: 'live', text }), naming the `kind` ("That run isn’t here: …").
//   const { entry, notice } = useLinkedEntry(openId, { ready, find, read, kind: 'run' });
//   inspector={entry && <RunInspector run={entry} />} … {notice && <Note tone={notice.tone}>{notice.text}</Note>}
export function useLinkedEntry(openId, { ready, find, read, kind = 'item' }) {
    const listed = openId && ready ? find?.(openId) || null : null;
    const [got, setGot] = useState(null);
    const readRef = useRef(read);
    readRef.current = read;
    const inList = !!listed;
    useEffect(() => {
        if (!openId || !ready || inList) return undefined;
        let live = true;
        setGot({ id: openId, state: 'reading' });
        Promise.resolve()
            .then(() => readRef.current?.(openId))
            .then((item) => { if (live) setGot({ id: openId, item: item || null, state: item ? 'found' : 'missing' }); })
            .catch((e) => { if (live) setGot({ id: openId, state: 'error', message: e?.message || null }); });
        return () => { live = false; };
    }, [openId, ready, inList]);
    const mine = !inList && got && String(got.id) === String(openId) ? got : null;
    let notice = null;
    if (mine?.state === 'missing') notice = { tone: 'live', text: `That ${kind} isn’t here: it may have been removed, or it isn’t one you can see.` };
    if (mine?.state === 'error') notice = { tone: 'live', text: mine.message || `That ${kind} could not be read.` };
    return { entry: listed || mine?.item || null, state: inList ? 'listed' : mine?.state || null, notice };
}
